import { useMemo } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import EmptyState from "@/components/base/EmptyState";
import ApiState from "@/components/base/ApiState";
import BusinessGrid from "@/components/feature/BusinessGrid";
import { getBusinesses } from "@/services/publicApi";
import { useApiResource } from "@/hooks/useApiResource";

export default function Featured() {
  const { data, loading, error, reload } = useApiResource(() => getBusinesses(), []);
  const businesses = useMemo(() => data ?? [], [data]);

  const featured = useMemo(() => businesses.filter((b) => b.isFeatured), [businesses]);

  return (
    <>
      <Seo
        title="Featured Listings — Spotlighted Local Businesses"
        description="Discover hand-picked and verified businesses featured on Mysore Yellow Pages for their quality, reviews and reliability across the Mysore region."
        keywords="featured businesses Mysore, verified listings, top rated local businesses"
        path="/featured-listings"
      />

      <div className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-10">
          <Breadcrumbs items={[{ label: "Featured Listings" }]} />
          <div className="mt-3 flex items-start gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-foreground-950">
              <i className="ri-vip-crown-2-line text-2xl"></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl md:text-4xl font-extrabold tracking-tight text-foreground-950">
                Featured listings
              </h1>
              <p className="mt-2 max-w-2xl text-sm md:text-base text-foreground-600">
                These businesses upgraded for priority visibility and hold high ratings from real customers.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-10 md:py-14">
        <ApiState
          loading={loading}
          error={error}
          onRetry={reload}
          skeleton={
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-80 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
              ))}
            </div>
          }
          isEmpty={featured.length === 0}
          empty={
            <EmptyState
              icon="ri-vip-crown-line"
              title="No featured listings yet"
              description="Featured spots are reserved for premium plans. Check back soon or upgrade your own listing."
              action={{ label: "View listing plans", to: "/listing-plans" }}
            />
          }
        >
          <BusinessGrid businesses={featured} />
        </ApiState>

        <div className="mt-12 rounded-lg bg-accent-900 p-6 md:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-foreground-950">
              <i className="ri-rocket-line"></i> Get featured
            </span>
            <h2 className="mt-4 font-heading text-xl md:text-3xl font-extrabold text-background-50">
              Want your business in this spotlight?
            </h2>
            <p className="mt-2 max-w-xl text-sm md:text-base text-accent-100/90">
              Upgrade to Premium or Elite for a verified badge, priority placement and a shot at homepage features.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/listing-plans"
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-price-tag-3-line"></i> Compare plans
            </Link>
            <Link
              to="/list-your-business"
              className="inline-flex items-center gap-2 rounded-md border border-accent-100/40 bg-accent-50/10 px-5 py-3 text-sm font-semibold text-background-50 hover:bg-accent-50/20 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-circle-line"></i> List business
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}